// Motion for the route-* sections, all opt-in by attribute and off with prefers-reduced-motion:
//   [data-rt-reveal]          fades/slides in when it enters the viewport ([data-rt-reveal-delay] in ms)
//   [data-rt-parallax="0.1"]  moves at a fraction of the scroll speed while on screen
//   video[data-rt-autoplay]   plays only while visible (saves battery and bandwidth), and not at all on reduced motion;
//                             loads from 600 px before it scrolls in, and retries a failed play() while visible
//   [data-rt-story]           sticky scroll story: sets data-step on the story from the step in view
(() => {
  if (window.__routeMotion) return;
  window.__routeMotion = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;
  root.classList.add('rt-js');

  const revealed = new WeakSet();
  const revealer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        const delay = parseInt(el.dataset.rtRevealDelay || '0', 10);
        el.style.transitionDelay = delay ? `${delay}ms` : '';
        el.classList.add('is-revealed');
        revealed.add(el);
        revealer.unobserve(el);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  // A play() that fails (no data yet, a slow or dropped request, interrupted by a scroll) is tried again while the
  // video stays on screen: when it can play, and every 1.5 s, so it never waits for a scroll away and back.
  const onScreen = new WeakSet();
  const retries = new WeakMap();
  const tryPlay = (video) => {
    clearTimeout(retries.get(video));
    if (!onScreen.has(video) || reduced.matches || !video.paused) return;
    if (video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) video.load();
    const play = video.play();
    if (play && play.catch) {
      play.catch(() => {
        if (onScreen.has(video)) retries.set(video, setTimeout(() => tryPlay(video), 1500));
      });
    }
  };
  const videos = new IntersectionObserver(
    (entries) => {
      for (const { target: video, isIntersecting } of entries) {
        if (isIntersecting) {
          onScreen.add(video);
          tryPlay(video);
        } else {
          onScreen.delete(video);
          clearTimeout(retries.get(video));
          video.pause();
        }
      }
    },
    { threshold: 0.2 }
  );
  // Start downloading a little before a video scrolls into view, so it's ready when it gets there
  const warm = new IntersectionObserver(
    (entries) => {
      for (const { target: video, isIntersecting } of entries) {
        if (!isIntersecting || reduced.matches) continue;
        video.preload = 'auto';
        warm.unobserve(video);
      }
    },
    { rootMargin: '600px 0px' }
  );
  const watched = new WeakSet();

  const parallax = new Set();
  let ticking = false;
  const updateParallax = () => {
    ticking = false;
    if (reduced.matches) return;
    const vh = window.innerHeight;
    for (const el of parallax) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) continue;
      const speed = parseFloat(el.dataset.rtParallax) || 0.1;
      const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
      el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    }
  };
  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateParallax);
    }
  };

  const stories = new Set();
  const storyObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const story = entry.target.closest('[data-rt-story]');
        if (story) story.dataset.step = entry.target.dataset.rtStep;
      }
    },
    { rootMargin: '-45% 0px -45% 0px' }
  );

  function scan(scope = document) {
    scope.querySelectorAll('[data-rt-reveal]').forEach((el) => {
      if (revealed.has(el)) return;
      if (reduced.matches) el.classList.add('is-revealed');
      else revealer.observe(el);
    });
    scope.querySelectorAll('video[data-rt-autoplay]').forEach((v) => {
      v.muted = true;
      if (!watched.has(v)) {
        watched.add(v);
        v.addEventListener('canplay', () => tryPlay(v));
      }
      videos.observe(v);
      warm.observe(v);
    });
    scope.querySelectorAll('[data-rt-parallax]').forEach((el) => parallax.add(el));
    scope.querySelectorAll('[data-rt-story] [data-rt-step]').forEach((el) => {
      if (!stories.has(el)) {
        stories.add(el);
        storyObserver.observe(el);
      }
    });
    updateParallax();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
  // Theme editor: sections are re-rendered in place
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  window.routeMotion = { scan };
})();
